import React from 'react'
import { t } from 'i18next'
import { hasText, sortBySequenceNumber } from 'shared/lib/common-util'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import TransitPreviewField from './TransitPreviewField'
import { ConsignmentItem } from '../../consignment-item/models/types'
import TransitPreviewArrayField from './TransitPreviewArrayField'
import { useFetchConsignmentItem } from '../../consignment-item/api/fetchConsignmentItem'
import { useFetchConsignmentItemDocuments } from '../../consignment-item-document/api/fetchConsignmentItemDocuments'
import LoadingOverlay from '../../../../components/p5/LoadingOverlay'
import { isPreviousDocument, isSupportingDocument, isTransportDocument } from '../../document/models/types'
import TransitPreviewDocumentsSection from './TransitPreviewDocumentsSection'
import TransitPreviewTransportDocuments from './TransitPreviewTransportDocuments'
import TransitPreviewPreviousDocuments from './TransitPreviewPreviousDocuments'
import TransitPreviewSupportingDocuments from './TransitPreviewSupportingDocuments'

interface ConsignmentItemsTableRowDetailsProps {
  consignmentItem: ConsignmentItem
}
function ConsignmentItemsTableRowDetails({
  consignmentItem,
}: ConsignmentItemsTableRowDetailsProps) {
  const [, countriesOptions] = useCodelist('COUNTRIES')
  const [, packagesOptions] = useCodelist('PACKAGES')

  const {
    data: consignmentItemDetails,
    isFetching: isConsignmentItemLoading,
  } = useFetchConsignmentItem(consignmentItem.id)
  const {
    data: consignmentItemDocumentsData,
    isFetching: isConsignmentItemDocumentsLoading,
  } = useFetchConsignmentItemDocuments(consignmentItem.id)

  const consignmentItemDocuments = consignmentItemDocumentsData ?? []

  const initialLoading = isConsignmentItemLoading || isConsignmentItemDocumentsLoading
  if (!consignmentItemDetails) {
    return (
      <LoadingOverlay loading>
        <div />
      </LoadingOverlay>
    )
  }

  const transportDocuments = consignmentItemDocuments.filter(isTransportDocument).sort(sortBySequenceNumber)
  const previousDocuments = consignmentItemDocuments.filter(isPreviousDocument).sort(sortBySequenceNumber)
  const supportingDocuments = consignmentItemDocuments.filter(isSupportingDocument).sort(sortBySequenceNumber)

  return (
    <LoadingOverlay loading={initialLoading} minHeight="auto">
      <div className="d-flex flex-wrap">
        <div className="col-6">
          <TransitPreviewField
            label={t('declaration.p5.commodityHarmonizedSystemSubHeadingCode')}
            value={consignmentItemDetails.commodityHarmonizedSystemSubHeadingCode ?? ''}
          />
          <TransitPreviewField
            label={t('declaration.p5.commodityCombinedNomenclatureCode')}
            value={hasText(consignmentItemDetails.commodityCombinedNomenclatureCode) ? consignmentItemDetails.commodityCombinedNomenclatureCode ?? '' : '-'}
          />
          <TransitPreviewField
            label={t('declaration.p5.goodsMeasureGrossMass')}
            value={`${consignmentItemDetails.goodsMeasureGrossMass}`}
          />
          <TransitPreviewField
            label={t('declaration.p5.goodsMeasureNetMass')}
            value={`${consignmentItem.goodsMeasureNetMass}`}
          />
          {
            consignmentItemDetails.consignee !== null && consignmentItemDetails.consignee.id !== null && (
              <TransitPreviewArrayField
                label={t('declaration.p5.consignee')}
                items={[
                  {
                    value: consignmentItemDetails.consignee.name,
                    parenthesesValue: consignmentItemDetails.consignee?.identificationNumber ?? null,
                  },
                  {
                    value: consignmentItemDetails.consignee?.address?.addressLine,
                    parenthesesValue: consignmentItemDetails.consignee?.address?.city,
                  },
                  {
                    value: countriesOptions.find((
                      option,
                    ) => option.value === consignmentItemDetails.consignee?.address?.country)?.label,
                    parenthesesValue: consignmentItemDetails.consignee?.address?.postcode,
                  },
                ]}
              />
            )
          }
          <TransitPreviewField
            label={t('declaration.p5.price')}
            value={consignmentItemDetails.price ? `${consignmentItemDetails.price} EUR` : '-'}
          />
          <TransitPreviewField
            label={t('declaration.p5.vatRate')}
            value={consignmentItemDetails.vatRate ? `${consignmentItemDetails.vatRate}%` : '-'}
          />
          <TransitPreviewField
            label={t('declaration.p5.dutyRate')}
            value={consignmentItemDetails.dutyRate ? `${consignmentItemDetails.dutyRate}%` : '-'}
          />
          <TransitPreviewField
            label={t('declaration.p5.excise')}
            value={consignmentItemDetails.excise ? `${consignmentItemDetails.excise} EUR` : '-'}
          />
        </div>
        <div className="col-6">
          {
            consignmentItemDetails.packaging.sort(sortBySequenceNumber).map((itemPackage, index) => (
              <div
                key={`package_${itemPackage.sequenceNumber}`}
                className={`${(index + 1) !== consignmentItemDetails.packaging.length ? 'mb-4' : ''} mb-1 `}
              >
                <TransitPreviewField
                  label={`${t('declaration.p5.packages', { context: 'singular' })} ${t('declaration.p5.sequenceNumber')}`}
                  value={`${(itemPackage.sequenceNumber ?? 0) + 1 ?? '-'}`}
                />
                <TransitPreviewField
                  label={t('declaration.p5.typeOfPackages')}
                  value={`${packagesOptions.find((option) => option.value === itemPackage.typeOfPackages)?.label}`}
                />
                <TransitPreviewField
                  label={t('declaration.p5.numberOfPackages')}
                  value={`${itemPackage.numberOfPackages ?? '-'}`}
                />
                <TransitPreviewField
                  label={t('declaration.p5.shippingMarks')}
                  value={`${itemPackage.shippingMarks}`}
                />
              </div>
            ))
          }
        </div>
      </div>
      <div className="d-flex flex-wrap">
        {transportDocuments.length > 0 && (
          <TransitPreviewDocumentsSection className="col-6">
            <TransitPreviewTransportDocuments transportDocuments={transportDocuments} />
          </TransitPreviewDocumentsSection>
        )}
        {supportingDocuments.length > 0 && (
          <TransitPreviewDocumentsSection className="col-6">
            <TransitPreviewSupportingDocuments supportingDocuments={supportingDocuments} />
          </TransitPreviewDocumentsSection>
        )}
        {previousDocuments.length > 0 && (
          <TransitPreviewDocumentsSection className="col-6">
            <TransitPreviewPreviousDocuments previousDocuments={previousDocuments} />
          </TransitPreviewDocumentsSection>
        )}
      </div>
    </LoadingOverlay>
  )
}

export default ConsignmentItemsTableRowDetails
